import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import SubmissionDetails from '../../../SubmissionDetails';
import { isExtendedDeviationFullyLoaded } from '../../../DeviationPage/redux/helpers';
import { normalizePremiumFolderData } from '../../../utils/normalizePremiumFolderData';
import TextRenderer from '../../../TextRenderer';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import { useDeviationViewContext } from '../../../Context/DeviationViewContext';
import SubPaywall from '../../../DeviationViews/Full/_partials/SubPaywall';
import TextContent from '../../Details/_partials/TextContent';
import StatBlock from '../../Details/_partials/StatBlock';
import PremiumCover from '../_partials/PremiumCover';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import LitViewerV3 from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/Literature';

import s from './Literature.scss';

export interface Props {
  deviation: PapiDeviation;
  withArtist?: boolean;
}

const LiteratureContent: React.FC<Props> = ({
  deviation: { textContent },
  deviation,
  withArtist = true,
}) => {
  const { t } = useTranslation();
  const isExtendedLoaded = isExtendedDeviationFullyLoaded(deviation);
  const { isPremiumLocked } = normalizePremiumFolderData(
    deviation?.premiumFolderData
  );
  const isMobile = useContext(MobileContext);
  const { isSubscribedButBlocked, needsSubscription } =
    useSubscriptionTier(deviation);
  const isBlocked = needsSubscription || isSubscribedButBlocked;

  // only show here in full screen, otherwise it's shown in DesktopLayout in the left column
  const { isFullscreen } = useDeviationViewContext();
  const shouldShowFullscreenSubPaywall = isBlocked && isFullscreen;

  return (
    <>
      {withArtist && (
        <div className={s['author-block']}>
          <SubmissionDetails deviation={deviation} />
          {/* include whitespace to force a non-zero height */}
          {!isMobile && ' '}
        </div>
      )}
      <StatBlock className={s['stats']} deviation={deviation} />

      <Section
        className={classnames(
          s['wrapper'],
          isPremiumLocked && s['wrapper-with-paywall']
        )}
      >
        <VisuallyHidden>
          <Heading>
            {t('deviationview.literature.a11y.heading.content')}
          </Heading>
        </VisuallyHidden>

        {!isBlocked &&
          isExtendedLoaded &&
          (isPremiumLocked ? (
            <>
              <TextRenderer
                className={s['text-content-pf']}
                textContent={textContent}
                viewerKey={deviation.deviationId}
                legacyTheme="journal"
                tipTapViewer={LitViewerV3}
                cookieType={MeasuredCookieType.EDITOR_LITERATURE}
                defaultSize={{
                  desktop: { width: 780, height: 40 },
                  mobile: { width: 380, height: 80 },
                }}
              />
              <PremiumCover deviation={deviation} />
            </>
          ) : (
            <TextContent
              className={s['text-content']}
              textContent={textContent}
              deviationId={deviation.deviationId}
            />
          ))}

        {shouldShowFullscreenSubPaywall && (
          <SubPaywall
            className={s['paywall']}
            deviation={deviation}
            showImage={false}
          />
        )}
      </Section>
    </>
  );
};
LiteratureContent.displayName = 'LiteratureContent';

export default LiteratureContent;
